import React, { useState, useEffect } from 'react';
import {
    Text,
    IconButton,
    Spinner,
    SpinnerSize,
    useTheme,
} from '@fluentui/react';
import { EventData } from '../models/CloudStatusModels';
import { getComponentEvents } from '../api/SDSApi';
import {
    getStatusColor,
    formatDateTime,
    isMobileWidth,
} from '../utils/StatusHelpers';
import '../styles/CommonStyles.css';

interface ComponentEventsTabProps {
    selectedComponentId: string | null;
}

const EventRow: React.FC<{ event: EventData }> = ({ event }) => {
    const theme = useTheme();
    const [isExpanded, setIsExpanded] = useState(false);
    const statusColor = getStatusColor(event.health);
    const isMobile = isMobileWidth();

    const eventFields = [
        { label: 'Event ID', value: event.eventId },
        { label: 'Environment Name', value: event.environmentName },
        { label: 'Event Type', value: event.eventType },
        { label: 'Service Source GUID', value: event.serviceSourceGuid },
        { label: 'Component GUID', value: event.componentGuid },
        { label: 'Component Index', value: event.componentIndex.toString() },
        { label: 'Status GUID', value: event.statusGuid },
        {
            label: 'Status Indicator Enum',
            value: event.statusIndicatorEnum.toString(),
        },
        { label: 'True Value', value: event.trueValue.toString() },
        { label: 'Additional Value', value: event.additionalValue.toString() },
        { label: 'Error Code', value: event.errorCode.toString() },
        { label: 'Event Date', value: formatDateTime(event.eventDate) },
    ];

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                        ? '40px 1fr 100px'
                        : '40px 1fr 120px 200px',
                    gap: isMobile ? '8px' : '12px',
                    padding: isMobile ? '12px 8px' : '12px 16px',
                    backgroundColor: theme.palette.white,
                    borderBottom: `1px solid ${theme.palette.neutralLight}`,
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    overflowX: 'hidden',
                }}
                onClick={handleToggle}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                        theme.palette.neutralLighter;
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = theme.palette.white;
                }}
            >
                <IconButton
                    iconProps={{
                        iconName: isExpanded ? 'ChevronDown' : 'ChevronRight',
                    }}
                    ariaLabel={
                        isExpanded
                            ? 'Collapse event details'
                            : 'Expand event details'
                    }
                    aria-expanded={isExpanded}
                    title={isExpanded ? 'Collapse' : 'Expand'}
                    styles={{
                        root: {
                            color: theme.palette.neutralPrimary,
                            width: '32px',
                            height: '32px',
                        },
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleToggle();
                    }}
                />
                <Text
                    variant="small"
                    styles={{
                        root: {
                            color: theme.palette.neutralPrimary,
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                        },
                    }}
                >
                    {event.eventType}
                </Text>
                <Text
                    variant="small"
                    styles={{
                        root: {
                            color: statusColor,
                            fontWeight: 600,
                            textAlign: 'center',
                        },
                    }}
                >
                    {event.health}
                </Text>
                {!isMobile && (
                    <Text
                        variant="small"
                        styles={{
                            root: {
                                color: theme.palette.neutralSecondary,
                                fontSize: '11px',
                                textAlign: 'right',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            },
                        }}
                    >
                        {formatDateTime(event.eventDate)}
                    </Text>
                )}
            </div>

            {/* Expanded Event Details - Form-like view */}
            {isExpanded && (
                <div
                    style={{
                        backgroundColor: theme.palette.neutralLighterAlt,
                        borderBottom: `2px solid ${theme.palette.neutralLight}`,
                        padding: '20px 24px 20px 56px',
                    }}
                >
                    <Text
                        variant="medium"
                        styles={{
                            root: {
                                fontWeight: 600,
                                color: theme.palette.themePrimary,
                                marginBottom: '16px',
                                display: 'block',
                            },
                        }}
                    >
                        Event Details
                    </Text>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns:
                                window.innerWidth <= 768 ? '1fr' : '1fr 1fr',
                            gap: '16px',
                        }}
                    >
                        {eventFields.map((field, index) => (
                            <div
                                key={index}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px',
                                }}
                            >
                                <Text
                                    variant="small"
                                    styles={{
                                        root: {
                                            fontWeight: 600,
                                            color: theme.palette
                                                .neutralSecondary,
                                        },
                                    }}
                                >
                                    {field.label}
                                </Text>
                                <div
                                    style={{
                                        padding: '8px 12px',
                                        backgroundColor: theme.palette.white,
                                        border: `1px solid ${theme.palette.neutralLight}`,
                                        borderRadius: '4px',
                                        fontFamily:
                                            field.label
                                                .toLowerCase()
                                                .includes('guid') ||
                                            field.label
                                                .toLowerCase()
                                                .includes('id')
                                                ? 'monospace'
                                                : 'inherit',
                                    }}
                                >
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralPrimary,
                                            },
                                        }}
                                    >
                                        {field.value}
                                    </Text>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export const ComponentEventsTab: React.FC<ComponentEventsTabProps> = ({
    selectedComponentId,
}) => {
    const theme = useTheme();
    const [filteredEvents, setFilteredEvents] = useState<EventData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadedComponentId, setLoadedComponentId] = useState<string | null>(
        null
    );

    // Fetch events when component selection changes
    useEffect(() => {
        let isCancelled = false;

        const fetchComponentEvents = async () => {
            // Only fetch if component selection has actually changed
            if (selectedComponentId === loadedComponentId) {
                return;
            }

            if (!selectedComponentId) {
                if (!isCancelled) {
                    setFilteredEvents([]);
                    setLoadedComponentId(null);
                }
                return;
            }

            if (!isCancelled) {
                setIsLoading(true);
            }

            try {
                const response = await getComponentEvents(
                    selectedComponentId,
                    24,
                    50
                );

                if (!isCancelled) {
                    if (response.success && response.events) {
                        // Convert string dates to Date objects
                        const eventsWithDates = response.events.map(
                            (event) => ({
                                ...event,
                                eventDate: new Date(event.eventDate),
                            })
                        );
                        setFilteredEvents(eventsWithDates);
                    } else {
                        console.error(
                            'Failed to fetch events:',
                            response.errorMessage
                        );
                        setFilteredEvents([]);
                    }
                    setLoadedComponentId(selectedComponentId);
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Error fetching component events:', error);
                if (!isCancelled) {
                    setFilteredEvents([]);
                    setIsLoading(false);
                }
            }
        };

        fetchComponentEvents();

        return () => {
            isCancelled = true;
        };
    }, [selectedComponentId]);

    // Show loading state
    if (isLoading) {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '32px',
                }}
            >
                <Spinner size={SpinnerSize.large} label="Loading events..." />
            </div>
        );
    }

    // Show no events found state
    if (filteredEvents.length === 0) {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '32px',
                }}
            >
                <Text
                    variant="medium"
                    styles={{
                        root: {
                            color: theme.palette.neutralSecondary,
                            textAlign: 'center',
                        },
                    }}
                >
                    No events found for this component
                </Text>
            </div>
        );
    }

    return (
        <div>
            {/* Event count */}
            <div
                style={{
                    padding: '12px 20px',
                    backgroundColor: theme.palette.neutralLighterAlt,
                    borderBottom: `1px solid ${theme.palette.neutralLight}`,
                }}
            >
                <Text
                    variant="small"
                    styles={{
                        root: {
                            color: theme.palette.neutralPrimary,
                        },
                    }}
                >
                    {filteredEvents.length} event
                    {filteredEvents.length !== 1 ? 's' : ''} found
                </Text>
            </div>

            {/* Column Headers */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: isMobileWidth()
                        ? '40px 1fr 100px'
                        : '40px 1fr 120px 200px',
                    gap: isMobileWidth() ? '8px' : '12px',
                    padding: isMobileWidth() ? '12px 8px' : '12px 16px',
                    backgroundColor: theme.palette.neutralLighter,
                    borderBottom: `2px solid ${theme.palette.neutralLight}`,
                    overflowX: 'hidden',
                }}
            >
                <div />
                <Text
                    variant="small"
                    styles={{
                        root: {
                            fontWeight: 700,
                            color: theme.palette.black,
                        },
                    }}
                >
                    Event Type
                </Text>
                <Text
                    variant="small"
                    styles={{
                        root: {
                            fontWeight: 700,
                            color: theme.palette.black,
                            textAlign: 'center',
                        },
                    }}
                >
                    Health
                </Text>
                {!isMobileWidth() && (
                    <Text
                        variant="small"
                        styles={{
                            root: {
                                fontWeight: 700,
                                color: theme.palette.neutralPrimary,
                                textAlign: 'right',
                            },
                        }}
                    >
                        Event Date
                    </Text>
                )}
            </div>

            {/* Event Rows */}
            <div>
                {filteredEvents.map((event) => (
                    <EventRow key={event.eventId} event={event} />
                ))}
            </div>
        </div>
    );
};
